import QRCode from "qrcode";

const BASE32_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

const getCrypto = () => {
  if (typeof globalThis !== "undefined" && globalThis.crypto) return globalThis.crypto;
  if (typeof window !== "undefined" && window.crypto) return window.crypto;
  throw new Error("Web Crypto API is not supported in this environment");
};

/**
 * Encode a Uint8Array into a Base32 string (RFC 4648)
 */
export function base32Encode(buffer) {
  let bits = 0;
  let value = 0;
  let output = "";

  for (let i = 0; i < buffer.length; i++) {
    value = (value << 8) | buffer[i];
    bits += 8;

    while (bits >= 5) {
      output += BASE32_CHARS[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }

  if (bits > 0) {
    output += BASE32_CHARS[(value << (5 - bits)) & 31];
  }

  return output;
}

/**
 * Decode a Base32 string into a Uint8Array (RFC 4648)
 */
export function base32Decode(base32) {
  const cleanBase32 = base32.toUpperCase().replace(/[\s-]/g, "").replace(/=+$/, "");
  let bits = 0;
  let value = 0;
  const result = [];

  for (let i = 0; i < cleanBase32.length; i++) {
    const val = BASE32_CHARS.indexOf(cleanBase32[i]);
    if (val === -1) {
      throw new Error(`Invalid Base32 character: ${cleanBase32[i]}`);
    }

    value = (value << 5) | val;
    bits += 5;

    if (bits >= 8) {
      result.push((value >>> (bits - 8)) & 255);
      bits -= 8;
    }
  }

  return new Uint8Array(result);
}

/**
 * Generate a secure random Base32 secret key (20 bytes / 160 bits = 32 base32 characters)
 */
export function generateBase32Secret(byteLength = 20) {
  const crypto = getCrypto();
  const randomBytes = new Uint8Array(byteLength);
  crypto.getRandomValues(randomBytes);
  return base32Encode(randomBytes);
}

/**
 * Generate standard OTPAuth URI for Google & Microsoft Authenticator
 */
export function generateOtpAuthUri(email, secret, issuer = "Jimini Logistics") {
  const encodedIssuer = encodeURIComponent(issuer);
  const encodedEmail = encodeURIComponent(email);
  return `otpauth://totp/${encodedIssuer}:${encodedEmail}?secret=${secret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=6&period=30`;
}

/**
 * Generate high-resolution QR Code data URL
 */
export async function generateQRCodeDataUrl(text) {
  try {
    return await QRCode.toDataURL(text, {
      width: 256,
      margin: 2,
      color: {
        dark: "#000000",
        light: "#ffffff"
      },
      errorCorrectionLevel: "M"
    });
  } catch (err) {
    console.error("Failed to generate QR Code:", err);
    throw err;
  }
}

/**
 * Compute 6-digit TOTP token at a given counter step
 */
export async function computeTokenAtCounter(secretBase32, counter) {
  const crypto = getCrypto();
  const keyBytes = base32Decode(secretBase32);

  // Import key into SubtleCrypto
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "HMAC", hash: { name: "SHA-1" } },
    false,
    ["sign"]
  );

  // Convert 64-bit integer counter to big-endian ArrayBuffer
  const buffer = new ArrayBuffer(8);
  const view = new DataView(buffer);
  view.setUint32(0, Math.floor(counter / 0x100000000), false);
  view.setUint32(4, counter >>> 0, false);

  // HMAC-SHA1
  const hmac = await crypto.subtle.sign("HMAC", cryptoKey, buffer);
  const hmacBytes = new Uint8Array(hmac);

  // Dynamic truncation (RFC 4226 Section 5.3)
  const offset = hmacBytes[hmacBytes.length - 1] & 0x0f;
  const binaryCode =
    ((hmacBytes[offset] & 0x7f) << 24) |
    ((hmacBytes[offset + 1] & 0xff) << 16) |
    ((hmacBytes[offset + 2] & 0xff) << 8) |
    (hmacBytes[offset + 3] & 0xff);

  const token = (binaryCode % 1000000).toString().padStart(6, "0");
  return token;
}

/**
 * Verify a 6-digit TOTP token with clock-drift tolerance window
 * @param {string} secret - Base32 secret key
 * @param {string} token - 6-digit user input code
 * @param {number} window - Number of 30-second steps to tolerate before/after (default 1 = ±30s)
 * @returns {Promise<boolean>}
 */
export async function verifyTOTP(secret, token, window = 1) {
  if (!secret || !token) return false;
  const cleanToken = token.toString().trim().replace(/\s+/g, "");
  if (cleanToken.length !== 6 || !/^\d{6}$/.test(cleanToken)) {
    return false;
  }

  const now = Date.now();
  const timeStep = 30 * 1000;
  const currentCounter = Math.floor(now / timeStep);

  for (let i = -window; i <= window; i++) {
    try {
      const expectedToken = await computeTokenAtCounter(secret, currentCounter + i);
      if (expectedToken === cleanToken) {
        return true;
      }
    } catch (e) {
      console.error("Error computing token at step:", e);
    }
  }

  return false;
}

/**
 * Generate 8 single-use emergency backup recovery codes (format: XXXX-XXXX)
 */
export function generateBackupCodes(count = 8) {
  const crypto = getCrypto();
  const chars = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ"; // excluded 0, O, 1, I for legibility
  const codes = [];
  const randomBytes = new Uint8Array(count * 8);
  crypto.getRandomValues(randomBytes);

  for (let i = 0; i < count; i++) {
    let code = "";
    for (let j = 0; j < 8; j++) {
      const byte = randomBytes[i * 8 + j];
      code += chars[byte % chars.length];
      if (j === 3) code += "-";
    }
    codes.push(code);
  }

  return codes;
}

/**
 * Hash a backup code using SHA-256 for secure storage
 */
export async function hashBackupCode(code) {
  const crypto = getCrypto();
  const cleanCode = code.toUpperCase().replace(/[\s-]/g, "");
  const encoder = new TextEncoder();
  const data = encoder.encode(cleanCode);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Validate and consume a backup code
 * @param {string} enteredCode - User provided recovery code
 * @param {string[]} storedHashedCodes - Array of SHA-256 hashed codes stored in database
 * @returns {Promise<{ valid: boolean, remainingHashedCodes: string[] }>}
 */
export async function verifyAndConsumeBackupCode(enteredCode, storedHashedCodes = []) {
  if (!enteredCode || !storedHashedCodes || storedHashedCodes.length === 0) {
    return { valid: false, remainingHashedCodes: storedHashedCodes || [] };
  }

  const enteredHash = await hashBackupCode(enteredCode);
  const index = storedHashedCodes.indexOf(enteredHash);

  if (index !== -1) {
    // Found and matched: remove the consumed code
    const remaining = [...storedHashedCodes];
    remaining.splice(index, 1);
    return { valid: true, remainingHashedCodes: remaining };
  }

  return { valid: false, remainingHashedCodes: storedHashedCodes };
}
